import {
  isRouteErrorResponse,
  Links,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router"
import { QueryClientProvider } from "@tanstack/react-query"
import * as Sentry from "@sentry/react-router"
import { GoogleOAuthProvider } from "@react-oauth/google"

import type { Route } from "./+types/root"
import { queryClient } from "./store/queryClient"
import "./app.css"
import { useEffect } from "react"
import { ensureFullstoryInitialized } from "~/lib/fullStory.client"
import { initMixPanel } from "~/lib/mixpanel.client"
import { Toaster } from "~/components/ui/sonner"

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
]

export function Layout({ children }: { children: React.ReactNode }) {
  const googleClientId: string = import.meta.env.VITE_GOOGLE_CLIENT_ID as string

  return (
    <html lang="en" style={{ fontSize: "16px" }}>
      <head>
        <title>Loan Dashboard</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/png" href="/favicon.png" />
        <Links />
      </head>
      <body>
        <GoogleOAuthProvider clientId={googleClientId || ""}>
          <QueryClientProvider client={queryClient}>
            {children}
            <Toaster />
          </QueryClientProvider>
        </GoogleOAuthProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function App() {
  useEffect(() => {
    ensureFullstoryInitialized()
    initMixPanel()
  }, [])

  return <Outlet />
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!"
  let details = "An unexpected error occurred."
  let stack: string | undefined

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error"
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details
  } else if (error && error instanceof Error) {
    Sentry.captureException(error)
    if (import.meta.env.DEV) {
      details = error.message
      stack = error.stack
    }
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
      <Toaster />
    </main>
  )
}
